import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
} from "@mui/material";
import {
  useEffect,
  useState,
} from "react";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import { formatDate } from "utils/formatDate";
import DocumentActions from "./DocumentActions";
import DocumentStatus from "./DocumentStatus";
import {
  decideName,
  workflowFile,
} from "../service/WorkflowService";
import { useCompanyDocumentsLanguage } from "../i18n/CompanyDocumentsLanguageContext";

const dateOnly = (value) => {
  if (!value) return "—";

  return (
    String(value).match(
      /^(\d{4}-\d{2}-\d{2})/,
    )?.[1] || "—"
  );
};

const getNameFile = (
  id,
  disposition,
) =>
  workflowFile(
    id,
    true,
    disposition === "inline",
  );

export default function CompanyProfileReviewModal({
  record,
  canReview,
  onClose,
  onSaved,
}) {
  const [busy, setBusy] =
    useState(false);

  const [previewLoading, setPreviewLoading] =
    useState(true);

  const [previewURL, setPreviewURL] =
    useState("");

  const [decision, setDecision] =
    useState("");

  const [note, setNote] =
    useState("");

  const [error, setError] =
    useState("");

  const { setMessage } =
    useSnackbar();

  const { t } =
    useCompanyDocumentsLanguage();

  useEffect(() => {
    let active = true;
    let url = "";

    setPreviewLoading(true);
    setError("");
    setDecision("");
    setNote("");

    workflowFile(
      record.id,
      true,
      true,
    )
      .then((blob) => {
        if (!active) return;

        url =
          URL.createObjectURL(blob);

        setPreviewURL(url);
      })
      .catch((e) => {
        if (active) {
          setError(e.message);
        }
      })
      .finally(() => {
        if (active) {
          setPreviewLoading(false);
        }
      });

    return () => {
      active = false;

      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [
    record.id,
  ]);

  const confirm = async () => {
    if (
      decision === "REJECTED" &&
      !note.trim()
    ) {
      return;
    }

    setBusy(true);
    setError("");

    try {
      await decideName(
        record.id,
        decision,
        note.trim(),
      );

      setMessage({
        type: "success",
        desc:
          decision === "APPROVED"
            ? t("adminReview.profile.approvedSuccess")
            : t("adminReview.profile.rejectedSuccess"),
      });

      await onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={
        busy
          ? undefined
          : onClose
      }
      aria-labelledby="company-profile-review-title"
      sx={{
        zIndex: 1000001,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform:
            "translate(-50%, -50%)",
          width: {
            xs:
              "calc(100% - 24px)",
            md: 850,
          },
          maxWidth:
            "calc(100vw - 24px)",
          maxHeight:
            "calc(100vh - 24px)",
          boxSizing:
            "border-box",
          overflowY: "auto",
          bgcolor:
            "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 3,
        }}
      >
        <h2
          id="company-profile-review-title"
          className="text-xl font-bold mb-4"
        >
          {t("adminReview.profile.title")}
        </h2>

        {error && (
          <Alert severity="error">
            {error}
          </Alert>
        )}

        <div className="grid gap-4">
          <div className="flex justify-between gap-3">
            <div>
              <h3 className="font-semibold">
                {record.company_name} ·{" "}
                {record.company_code}
              </h3>

              <p>
                {t("adminReview.profile.companyNameChange")}
              </p>
            </div>

            <DocumentStatus
              status={
                record.status
              }
            />
          </div>

          <p>
            {t("adminReview.profile.companyContext")}
          </p>

          <div className="text-sm break-words">
            <p>
              {t("adminReview.common.filename")}:{" "}
              {record.stored_filename}
            </p>

            <p>
              {t("adminReview.profile.submittedBy")}:{" "}
              {record.requester_name ||
                t("adminReview.common.user", {
                  id: record.requested_by,
                })}
            </p>

            <p>
              {t("adminReview.profile.submitted")}:{" "}
              {formatDate.toFullDateTime(
                record.submitted_at,
              )}
            </p>
          </div>

          {record.reviewed_at && (
            <div className="text-sm">
              <p>
                {t("adminReview.common.reviewedBy")}:{" "}
                {t("adminReview.common.admin", {
                  id: record.reviewed_by,
                })}
              </p>

              <p>
                {t("adminReview.common.reviewed")}:{" "}
                {formatDate.toFullDateTime(
                  record.reviewed_at,
                )}
              </p>

              {record.review_note && (
                <p className="whitespace-pre-wrap break-words">
                  {t("adminReview.common.noteFromSigu")}:{" "}
                  {record.review_note}
                </p>
              )}
            </div>
          )}

          <div className="text-sm">
            <p>
              {t("adminReview.profile.currentName")}:{" "}
              <strong>
                {record.old_name ||
                  "—"}
              </strong>
            </p>

            <p>
              {t("adminReview.profile.proposedName")}:{" "}
              <strong>
                {record.proposed_name ||
                  "—"}
              </strong>
            </p>

            <p>
              {t("adminReview.profile.effectiveDate")}:{" "}
              <strong>
                {dateOnly(
                  record.effective_date,
                )}
              </strong>
            </p>
          </div>

          <div>
            <DocumentActions
              document={record}
              getFile={getNameFile}
              showView={false}
              downloadVariant="outlined"
            />
          </div>

          <div>
            <p className="font-semibold mb-2">
              {t("adminReview.profile.pdfPreview")}
            </p>

            {previewLoading ? (
              <CircularProgress
                size={24}
              />
            ) : (
              previewURL && (
                <iframe
                  title={t("adminReview.profile.pdfTitle")}
                  src={previewURL}
                  style={{
                    width: "100%",
                    height: "55vh",
                    border:
                      "1px solid #E2E8F0",
                  }}
                />
              )
            )}
          </div>

          {canReview &&
            record.status ===
              "SUBMITTED" &&
            !decision && (
              <div className="flex gap-3">
                <Button
                  variant="outlined"
                  onClick={() =>
                    setDecision(
                      "REJECTED",
                    )
                  }
                >
                  {t("adminReview.profile.reject")}
                </Button>

                <Button
                  variant="contained"
                  onClick={() =>
                    setDecision(
                      "APPROVED",
                    )
                  }
                >
                  {t("adminReview.profile.approve")}
                </Button>
              </div>
            )}

          {canReview &&
            decision && (
              <div className="border rounded-lg p-4 grid gap-3">
                <h3 className="font-semibold">
                  {decision ===
                  "APPROVED"
                    ? t("adminReview.profile.approveQuestion")
                    : t("adminReview.profile.rejectQuestion")}
                </h3>

                {decision ===
                  "REJECTED" && (
                  <TextField
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
                        alignItems: "flex-start",
                        padding: "16.5px 14px",
                      },
                      "& .MuiInputBase-inputMultiline": {
                        padding: "0 !important",
                      },
                    }}
                    label={t("adminReview.profile.reviewNote")}
                    required
                    multiline
                    minRows={3}
                    inputProps={{
                      maxLength:
                        2000,
                    }}
                    helperText={t(
                      "adminReview.profile.rejectHelper",
                    )}
                    value={note}
                    disabled={busy}
                    onChange={(
                      e,
                    ) =>
                      setNote(
                        e.target
                          .value,
                      )
                    }
                  />
                )}

                <div className="flex gap-3">
                  <Button
                    disabled={busy}
                    onClick={() => {
                      setDecision(
                        "",
                      );
                      setNote("");
                    }}
                  >
                    {t("adminReview.profile.cancel")}
                  </Button>

                  <Button
                    variant="contained"
                    disabled={
                      busy ||
                      (decision ===
                        "REJECTED" &&
                        !note.trim()) ||
                      Array.from(
                        note,
                      ).length >
                        2000
                    }
                    onClick={confirm}
                  >
                    {decision ===
                    "APPROVED"
                      ? t("adminReview.profile.confirmApprove")
                      : t("adminReview.profile.confirmReject")}
                  </Button>
                </div>
              </div>
            )}
        </div>

        <div className="flex justify-end mt-4">
          <Button
            disabled={busy}
            onClick={onClose}
          >
            {t("adminReview.common.close")}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
